import React from 'react';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { Page, LayoutSingleColumn, NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import {
  DIGO_CLIENT_USER_TYPE,
  DIGO_WORKER_USER_TYPE,
  signupLinkProps,
} from '../LandingPage/digoServices';

import css from './AboutPage.module.css';

// Design: Francis Pelletier (BOLÉAN), maquette Claude Design « DIGO À propos ».
// Texts are the ones already confirmed by the client. The scrolling ribbon
// (.ribbon-holder) of the mock-up is intentionally NOT implemented.
const IMG = '/static/about';

const VALUES = [
  {
    key: 'v1',
    title: "L'humain d'abord",
    desc:
      "Nous plaçons le respect, l'écoute et la qualité des relations au cœur de l'expérience, parce que nous ne traitons pas que des demandes : nous répondons aux besoins de vraies personnes.",
  },
  {
    key: 'v2',
    title: 'La simplicité',
    desc:
      "Nous concevons chaque étape pour qu'elle soit claire, intuitive et immédiate. Trouver de l'aide ou offrir ses services ne doit jamais être compliqué.",
  },
  {
    key: 'v3',
    title: 'La confiance',
    desc:
      'Nous bâtissons un environnement transparent où les clients et les travailleurs interagissent de façon responsable et sécuritaire.',
  },
  {
    key: 'v4',
    title: "La flexibilité et l'accessibilité",
    desc:
      'Notre modèle valorise la liberté absolue de choisir quand, comment et pour quels services utiliser la plateforme.',
  },
  {
    key: 'v5',
    title: "L'innovation utile",
    desc:
      "Nous exploitons la technologie et l'intelligence artificielle pour résoudre de vrais problèmes et rendre chaque connexion plus rapide et pertinente.",
  },
];

const ValueCard = ({ value, index, lead }) => (
  <article
    className={[css.vcard, lead ? css.vcardLead : null, css[value.key]].filter(Boolean).join(' ')}
  >
    <span className={css.vcardNum}>{String(index + 1).padStart(2, '0')}</span>
    <h3 className={css.vcardTitle}>{value.title}</h3>
    <p className={css.vcardText}>{value.desc}</p>
  </article>
);

export const AboutPageComponent = ({ scrollingDisabled }) => {
  return (
    <Page
      title="À propos — DIGO"
      description="Des besoins réels, des humains disponibles, une connexion simple. Découvrez la vision, la mission et les valeurs de DIGO, la plateforme québécoise des services de proximité."
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          {/* ─── HERO ─────────────────────────────────────────── */}
          <section className={css.hero}>
            <div className={css.wrap}>
              <div className={css.heroText}>
                <span className={css.eyebrow}>À propos de DIGO</span>
                <h1 className={css.heroTitle}>
                  Des besoins réels. Des <span className={css.highlight}>humains</span> disponibles.
                  Une connexion simple.
                </h1>
                <p className={css.heroLead}>
                  Nous simplifions l&apos;accès aux services du quotidien en reliant directement
                  ceux qui ont un besoin avec ceux qui possèdent le temps et le savoir-faire pour y
                  répondre. Notre plateforme propulse une économie flexible où la technologie
                  facilite les connexions locales et humaines sans jamais les dénaturer.
                </p>
              </div>
              <div className={css.heroPhotos}>
                <figure className={`${css.hp} ${css.hpA}`}>
                  <img
                    src={`${IMG}/corridor.webp`}
                    alt="Deux membres de l'équipe DIGO discutent en marchant dans un corridor"
                  />
                  <span className={css.chip}>Local.</span>
                </figure>
                <figure className={`${css.hp} ${css.hpB}`}>
                  <img
                    src={`${IMG}/reunion.webp`}
                    alt="L'équipe DIGO en rencontre autour d'une grande table"
                  />
                  <span className={`${css.chip} ${css.chipPurple}`}>Fiable.</span>
                </figure>
                <figure className={`${css.hp} ${css.hpC}`}>
                  <img
                    src={`${IMG}/telephone.webp`}
                    alt="Un membre de l'équipe DIGO au téléphone, souriant"
                  />
                  <span className={`${css.chip} ${css.chipDark}`}>Digo.</span>
                </figure>
              </div>
            </div>
          </section>

          {/* ─── LE MOT DE LA FONDATRICE ──────────────────────── */}
          <section className={css.founder}>
            <div className={`${css.wrap} ${css.founderGrid}`}>
              <div className={css.founderMedia}>
                <div className={css.founderPhoto}>
                  <img
                    src={`${IMG}/justine-portrait.webp`}
                    alt="Justine Bellefleur devant les bureaux de DIGO"
                  />
                </div>
              </div>
              <div className={css.founderText}>
                <span className={css.eyebrow}>Le mot de la fondatrice</span>
                <p className={css.founderQuote}>
                  «&nbsp;J&apos;ai créé DIGO parce que trouver quelqu&apos;un de fiable pour un
                  petit travail ne devrait pas être un casse-tête. On voulait quelque chose de
                  local, humain et sans complication — pour les clients comme pour les
                  travailleurs.&nbsp;»
                </p>
                <div className={css.founderSign}>
                  <span className={css.founderDot} aria-hidden="true" />
                  <div>
                    <span className={css.founderName}>Justine Bellefleur</span>
                    <span className={css.founderRole}>Fondatrice, DIGO</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ─── VISION / MISSION ─────────────────────────────── */}
          <section className={css.pillars}>
            <div className={css.wrap}>
              <div className={css.pillarsGrid}>
                <div className={css.pillarsPhoto}>
                  <img
                    src={`${IMG}/sourire-table.webp`}
                    alt="Un membre de l'équipe DIGO rit pendant une rencontre"
                  />
                </div>
                <div className={`${css.pillar} ${css.pillarVision}`}>
                  <span className={`${css.eyebrow} ${css.eyebrowLime}`}>Notre vision</span>
                  <p>
                    Devenir la plateforme québécoise de référence pour les services ponctuels et de
                    proximité.
                  </p>
                </div>
                <div className={`${css.pillar} ${css.pillarMission}`}>
                  <span className={css.eyebrow}>Notre mission</span>
                  <p>
                    Connecter les besoins aux bonnes personnes, simplement et rapidement. Nous
                    offrons aux clients un accès direct à des compétences locales, et nous donnons
                    aux travailleurs une liberté totale de générer des revenus supplémentaires selon
                    leurs propres horaires. Nous transformons la façon dont les gens trouvent de
                    l&apos;aide en imposant un nouveau réflexe.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* ─── VALEURS ──────────────────────────────────────── */}
          <section className={css.values}>
            <div className={css.wrap}>
              <h2 className={css.valuesTitle}>Nos valeurs</h2>
              <div className={css.valuesGrid}>
                <figure className={`${css.vphoto} ${css.vP1}`}>
                  <img
                    src={`${IMG}/sourire-facade.webp`}
                    alt="Sourire d'un membre de l'équipe DIGO devant l'immeuble"
                  />
                </figure>
                {VALUES.map((v, i) => (
                  <ValueCard key={v.key} value={v} index={i} lead={i === 0} />
                ))}
                <figure className={`${css.vphoto} ${css.vP2}`}>
                  <img
                    src={`${IMG}/justine-assise.webp`}
                    alt="Justine Bellefleur, fondatrice de DIGO"
                  />
                </figure>
              </div>
            </div>
          </section>

          {/* ─── AMBITION ─────────────────────────────────────── */}
          <section className={css.ambition}>
            <div className={`${css.wrap} ${css.ambitionGrid}`}>
              <div>
                <span className={css.eyebrow}>Notre ambition</span>
                <h2 className={css.ambitionTitle}>
                  Propulser l&apos;économie de l&apos;entraide au Québec
                </h2>
                <p className={css.ambitionText}>
                  Nous bâtissons une communauté forte, capable de croître et de s&apos;étendre à de
                  multiples régions et catégories de services. En utilisant la technologie comme un
                  levier, nous facilitons les rencontres à grande échelle et nous permettons à un
                  maximum de personnes d&apos;accéder à des occasions de revenus. Notre modèle
                  prouve que les compétences, le temps et le savoir-faire de chacun possèdent une
                  valeur réelle et immédiate pour la communauté.
                </p>
              </div>
              <figure className={css.ambitionPhoto}>
                <img
                  src={`${IMG}/marche-trottoir.webp`}
                  alt="L'équipe DIGO en marche devant les bureaux, en pleine discussion"
                />
              </figure>
            </div>
          </section>

          {/* ─── MANIFESTE + CTA ──────────────────────────────── */}
          <section className={css.manifesto}>
            <div className={`${css.wrap} ${css.manifestoInner}`}>
              <h2 className={css.manifestoTitle}>
                Faire avancer les choses, <em>humainement.</em>
              </h2>
              <p className={css.manifestoText}>
                L&apos;entraide de proximité a une valeur inestimable, et nous l&apos;avons rendue
                instantanée. Nous éliminons les recherches longues et les complications pour créer
                un écosystème où chaque compétence trouve son besoin, et où chaque besoin trouve son
                humain. DIGO, c&apos;est l&apos;outil moderne, fiable et rassembleur qui redonne le
                pouvoir d&apos;agir à la communauté.
              </p>
              <div className={css.ctas}>
                <NamedLink
                  {...signupLinkProps(DIGO_CLIENT_USER_TYPE)}
                  className={`${css.cta} ${css.ctaPrimary}`}
                >
                  Publier une demande
                </NamedLink>
                <NamedLink
                  {...signupLinkProps(DIGO_WORKER_USER_TYPE)}
                  className={`${css.cta} ${css.ctaSecondary}`}
                >
                  Devenir travailleur
                </NamedLink>
              </div>
            </div>
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const AboutPage = connect(mapStateToProps)(AboutPageComponent);

export default AboutPage;
